import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuGroup, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuShortcut, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Separator } from "@/components/ui/separator";
import { Whatsapp, WhatsappStatus } from "@/interfaces/whatsapp";
import { cn } from "@/lib/utils";
import { IconBrandWhatsapp, IconTrash } from "@tabler/icons-react";
import { useQueryClient } from "@tanstack/react-query";
import { EllipsisVertical } from "lucide-react";
import { selectSetCurrent, selectSetOpen, useConnectionStore } from "../store";

type ConnectionCardProps = {
  whatsapp: Whatsapp;
}

type StatusBadge = {
  text: string;
  className: string;
}

const connectionStatus = new Map<WhatsappStatus, StatusBadge>([
  [WhatsappStatus.CONNECTED, { text: 'conectado', className: 'border border-green-300 bg-green-50 dark:border-green-700 dark:bg-green-950' }],
  [WhatsappStatus.CONNECTING, { text: 'connectando', className: 'border border-yellow-300 bg-yellow-50 dark:border-yellow-700 dark:bg-yellow-950' }],
  [WhatsappStatus.DISCONNECTED, { text: 'desconectado', className: 'border border-red-300 bg-red-50 dark:border-red-700 dark:bg-red-950' }],
])

export function ConnectionCard({ whatsapp }: ConnectionCardProps) {
  const status = connectionStatus.get(whatsapp.status);
  const queryClient = useQueryClient();
  const setCurrent = useConnectionStore(selectSetCurrent);
  const setOpen = useConnectionStore(selectSetOpen);

  function handleEdit() {
    setCurrent({
      id: whatsapp.id,
      name: whatsapp.name,
    })
    setOpen('update');
  }

  function handleDelete() {
    setCurrent({
      id: whatsapp.id,
      name: whatsapp.name,
    })
    setOpen('delete');
  }

  return (
    <li
      key={whatsapp.name}
      className='p-4 border rounded-lg hover:shadow-md'
    >
      <div className='flex items-center justify-between mb-8'>
        <div
          className={`flex size-10 items-center justify-center rounded-lg bg-muted p-2`}
        >
          <IconBrandWhatsapp />
        </div>
        <div className='flex gap-2'>
          <div
            className={cn(
              "inline-flex items-center justify-center gap-2 whitespace-nowrap font-medium transition-colors border border-input bg-background shadow-sm h-8 rounded-md px-3 text-xs",
              status?.className,
            )}
          >
            {status?.text}
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className='w-8'>
                <EllipsisVertical />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-40">
              <DropdownMenuLabel>Ações</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuGroup>
                <DropdownMenuItem onClick={handleEdit}>
                  Editar
                </DropdownMenuItem>
                <DropdownMenuItem>
                  Reiniciar
                </DropdownMenuItem>
                <Separator />
                <DropdownMenuItem onClick={handleDelete}>
                  Remover
                  <DropdownMenuShortcut>
                    <IconTrash size={16} />
                  </DropdownMenuShortcut>
                </DropdownMenuItem>
              </DropdownMenuGroup>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      <div>
        <h2 className='mb-1 font-semibold'>{whatsapp.name}</h2>
        <p className='text-gray-500 line-clamp-2'>{whatsapp.phone}</p>
      </div>
    </li>
  )
}